import { useEffect } from 'react'
import { create } from 'zustand'
import { applySharedDraft, useUi } from '../store/ui'

type SharedDraft = { target: string; text: string; revision: number; updated_at: number }
type PendingDraft = {
  text: string
  base_revision: number
  base_text?: string
  submitted?: string[]
}
type SyncCache = {
  revisions: Record<string, number>
  texts: Record<string, string>
  pending: Record<string, PendingDraft>
}
const ENDPOINT = '/api/ui_prefs/composer_drafts'
const CACHE_KEY = 'lane-control-ui:shared-drafts'

function loadCache(): SyncCache {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null')
    if (value && typeof value === 'object' && 'revisions' in value && 'pending' in value) {
      const revisions: Record<string, number> = {}
      const texts: Record<string, string> = {}
      const pending: Record<string, PendingDraft> = {}
      if (value.revisions && typeof value.revisions === 'object') {
        for (const [key, revision] of Object.entries(value.revisions)) {
          if (Number.isSafeInteger(revision) && revision >= 0) revisions[key] = revision
        }
      }
      if ('texts' in value && value.texts && typeof value.texts === 'object') {
        for (const [key, text] of Object.entries(value.texts)) {
          if (typeof text === 'string') texts[key] = text
        }
      }
      if (value.pending && typeof value.pending === 'object') {
        for (const [key, draft] of Object.entries(value.pending)) {
          if (draft && typeof draft === 'object' && 'text' in draft && typeof draft.text === 'string'
            && 'base_revision' in draft && typeof draft.base_revision === 'number'
            && Number.isSafeInteger(draft.base_revision) && draft.base_revision >= 0) {
            pending[key] = {
              text: draft.text, base_revision: draft.base_revision,
              ...('base_text' in draft && typeof draft.base_text === 'string' ? { base_text: draft.base_text } : {}),
              ...('submitted' in draft && Array.isArray(draft.submitted)
                ? { submitted: draft.submitted.filter((text: unknown): text is string => typeof text === 'string').slice(-8) } : {}),
            }
          }
        }
      }
      return { revisions, texts, pending }
    }
  } catch { /* Old drafts remain in the UI store. */ }
  // Import old local copies without treating them as newer than a shared draft.
  return { revisions: {}, texts: {}, pending: Object.fromEntries(
    Object.entries(useUi.getState().draftsByChat).map(([key, text]) => [key, { text, base_revision: 0 }]),
  ) }
}

const cache = loadCache()
let applyingRemote = false
let syncing: Promise<void> | null = null
let syncRequested = false
let timer: ReturnType<typeof setTimeout> | null = null

export const useDraftSync = create<{
  conflicts: Record<string, SharedDraft>
  offline: boolean
}>(() => ({ conflicts: {}, offline: false }))

function saveCache() {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)) } catch { /* Keep unsaved text in memory. */ }
}

function applyDraft(draft: SharedDraft) {
  applyingRemote = true
  try { applySharedDraft(draft.target, draft.text, draft.updated_at) }
  finally { applyingRemote = false }
}

// Recover edits whose local UI snapshot had not flushed before the app closed.
for (const [target, pending] of Object.entries(cache.pending)) {
  applyDraft({ target, text: pending.text, revision: pending.base_revision, updated_at: Date.now() })
}

function parseDraft(value: unknown): SharedDraft {
  if (!value || typeof value !== 'object'
    || !('target' in value) || typeof value.target !== 'string'
    || !('text' in value) || typeof value.text !== 'string'
    || !('revision' in value) || typeof value.revision !== 'number'
    || !Number.isSafeInteger(value.revision) || value.revision < 0
    || !('updated_at' in value) || typeof value.updated_at !== 'number') throw new Error('Invalid shared draft')
  return { target: value.target, text: value.text, revision: value.revision, updated_at: value.updated_at }
}

function forgetConflict(target: string) {
  if (!useDraftSync.getState().conflicts[target]) return
  const conflicts = { ...useDraftSync.getState().conflicts }
  delete conflicts[target]
  useDraftSync.setState({ conflicts })
}

function receiveDraft(shared: SharedDraft) {
  if (shared.revision < (cache.revisions[shared.target] ?? 0)) return
  cache.revisions[shared.target] = shared.revision
  cache.texts[shared.target] = shared.text
  const pending = cache.pending[shared.target]
  if (pending && pending.text !== shared.text) {
    if (pending.base_revision === shared.revision) return
    if (pending.base_text === shared.text || pending.submitted?.includes(shared.text)) {
      // Same content, or our own save whose response was lost. Continue typing.
      cache.pending[shared.target] = {
        text: pending.text, base_revision: shared.revision, base_text: shared.text,
      }
      forgetConflict(shared.target)
      syncRequested = true
    } else {
      useDraftSync.setState({ conflicts: { ...useDraftSync.getState().conflicts, [shared.target]: shared } })
    }
    return
  }
  delete cache.pending[shared.target]
  forgetConflict(shared.target)
  if ((useUi.getState().draftsByChat[shared.target] ?? '') !== shared.text) applyDraft(shared)
}

export function resolveDraftConflict(target: string, choice: 'local' | 'shared') {
  const shared = useDraftSync.getState().conflicts[target]
  if (!shared) return
  if (choice === 'local') {
    cache.pending[target] = {
      text: useUi.getState().draftsByChat[target] ?? '',
      base_revision: shared.revision, base_text: shared.text,
    }
  } else {
    delete cache.pending[target]
    applyDraft(shared)
  }
  forgetConflict(target)
  saveCache()
  void syncComposerDrafts()
}

async function syncPass() {
  for (const target of Object.keys(cache.pending)) {
    const pending = cache.pending[target]
    if (!pending || useDraftSync.getState().conflicts[target]) continue
    // Persist attempted content before sending: a lost response must not turn
    // the next edit into a conflict with this device's own committed save.
    pending.submitted = [...new Set([...(pending.submitted ?? []), pending.text])].slice(-8)
    saveCache()
    const body = JSON.stringify({ target, text: pending.text, base_revision: pending.base_revision })
    const response = await fetch(ENDPOINT, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body, signal: AbortSignal.timeout(10_000),
      // Chromium limits outstanding keepalive bodies to 64 KiB, including UTF-8.
      keepalive: new TextEncoder().encode(body).byteLength <= 60_000,
    })
    if (!response.ok && response.status !== 409) throw new Error('Draft sync unavailable')
    const result: unknown = await response.json()
    if (!result || typeof result !== 'object' || !('draft' in result)) throw new Error('Invalid draft response')
    const shared = parseDraft(result.draft)
    if (response.ok && cache.pending[target] !== pending && cache.pending[target]
      && shared.revision >= (cache.revisions[target] ?? 0)) {
      // Edits made during this accepted upload continue from its actual result.
      cache.pending[target] = {
        text: cache.pending[target].text, base_revision: shared.revision, base_text: shared.text,
      }
      syncRequested = true
    }
    receiveDraft(shared)
    saveCache()
  }
  const response = await fetch(ENDPOINT, { credentials: 'include', signal: AbortSignal.timeout(10_000) })
  if (!response.ok) throw new Error('Draft sync unavailable')
  const result: unknown = await response.json()
  if (!result || typeof result !== 'object' || !('drafts' in result) || !Array.isArray(result.drafts)) {
    throw new Error('Invalid drafts response')
  }
  for (const value of result.drafts) receiveDraft(parseDraft(value))
  saveCache()
  useDraftSync.setState({ offline: false })
}

export function syncComposerDrafts(): Promise<void> {
  syncRequested = true
  if (syncing) return syncing
  if (timer) { clearTimeout(timer); timer = null }
  syncing = (async () => {
    do {
      syncRequested = false
      await syncPass()
    } while (syncRequested)
  })().catch(() => {
    useDraftSync.setState({ offline: true })
  }).finally(() => { syncing = null })
  return syncing
}

// Observe the composer immediately; only disk snapshots and network writes debounce.
useUi.subscribe((state, previous) => {
  if (applyingRemote || state.draftsByChat === previous.draftsByChat) return
  let changed = false
  for (const target of new Set([...Object.keys(state.draftsByChat), ...Object.keys(previous.draftsByChat)])) {
    const text = state.draftsByChat[target] ?? ''
    if (text === (previous.draftsByChat[target] ?? '')) continue
    const conflict = useDraftSync.getState().conflicts[target]
    const pending = cache.pending[target]
    cache.pending[target] = conflict ? {
      text, base_revision: conflict.revision, base_text: conflict.text,
    } : {
      text, base_revision: pending?.base_revision ?? cache.revisions[target] ?? 0,
      base_text: pending?.base_text ?? cache.texts[target] ?? previous.draftsByChat[target] ?? '',
      submitted: pending?.submitted,
    }
    // Editing or deleting after seeing both copies explicitly prefers this edit.
    forgetConflict(target)
    changed = true
  }
  if (!changed) return
  saveCache()
  if (syncing) syncRequested = true
  // Bound save latency even during continuous typing.
  if (!timer) timer = setTimeout(() => { timer = null; void syncComposerDrafts() }, 150)
})

export function useComposerDraftSync() {
  useEffect(() => {
    const sync = () => { if (document.visibilityState !== 'hidden') void syncComposerDrafts() }
    const flush = () => { void syncComposerDrafts() }
    sync()
    const interval = window.setInterval(sync, 3_000)
    window.addEventListener('focus', sync)
    window.addEventListener('online', sync)
    window.addEventListener('pagehide', flush)
    window.addEventListener('agentchat:composer-drafts-changed', sync)
    document.addEventListener('visibilitychange', flush)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', sync)
      window.removeEventListener('online', sync)
      window.removeEventListener('pagehide', flush)
      window.removeEventListener('agentchat:composer-drafts-changed', sync)
      document.removeEventListener('visibilitychange', flush)
    }
  }, [])
}
